import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  Icon,
  Switch,
  Tooltip,
} from "@smartfaster-ui/components";
import { useInteriorDesigner } from "./context/InteriorDesignerContext";
import { ActionBar } from "./ActionBar";
import { hasHistory } from "./context/selectors";
import { useFurnitureToggle } from "./hooks/useFurnitureToggle";

type WayfindingControlsProps = {
  /**
   * When true the controls overlay the canvas (desktop). When false they sit
   * in their own panel (compact) and capture pointer events directly.
   */
  overlay?: boolean;
};

export function WayfindingControls({
  overlay = false,
}: WayfindingControlsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state, dispatch } = useInteriorDesigner();

  const isGenerating = state.generation.status === "loading";
  const showHistory = hasHistory({ state });

  const handleBackClick = () => navigate(-1);

  const handleHistoryClick = () => {
    dispatch({ type: "PANEL_OPENED", panel: "history" });
  };

  const {
    isVisible: showFurnitureToggle,
    canToggle: canToggleFurniture,
    furnitureVisible,
    toggle: handleFurnitureToggle,
  } = useFurnitureToggle();

  return (
    <Flex
      width="fill"
      height="fill"
      paddingX="sm"
      paddingY="md"
      direction="column"
      justify="between"
      pointerEvents={overlay ? "none" : "auto"}
    >
      <Flex justify="between" align="start">
        <Button
          variant="filled"
          color="surfaceNeutralPrimary"
          size="md"
          leading={<Icon name="ArrowLeft01Icon" />}
          onClick={handleBackClick}
          style={{ pointerEvents: "auto" }}
        >
          {t("interiorDesigner.wayfinding.back")}
        </Button>
        <Button
          variant="filled"
          color="surfaceNeutralPrimary"
          size="md"
          leading={<Icon name="Clock01Icon" />}
          onClick={handleHistoryClick}
          disabled={isGenerating || !showHistory}
          style={{ pointerEvents: "auto" }}
        >
          {t("interiorDesigner.wayfinding.history")}
        </Button>
      </Flex>

      <Flex justify={showFurnitureToggle ? "between" : "end"} align="end">
        {showFurnitureToggle && (
          <Tooltip
            content={
              !canToggleFurniture
                ? t("interiorDesigner.furniture.tooltipNoFurniture")
                : furnitureVisible
                  ? t("interiorDesigner.furniture.tooltipRemove")
                  : t("interiorDesigner.furniture.tooltipRestore")
            }
            side="right"
          >
            <Button
              variant="filled"
              color="surfaceNeutralPrimary"
              leading={<Icon name="Cabinet01Icon" color="foregroundPrimary" />}
              disabled={isGenerating || !canToggleFurniture}
              onClick={handleFurnitureToggle}
              trailing={
                <Box as="span" pointerEvents="none" inline aria-hidden>
                  <Switch
                    size="sm"
                    variant="primary"
                    checked={canToggleFurniture && furnitureVisible}
                    disabled={isGenerating || !canToggleFurniture}
                  />
                </Box>
              }
              style={{ pointerEvents: "auto" }}
            >
              {t("interiorDesigner.furniture.label")}
            </Button>
          </Tooltip>
        )}

        <ActionBar />
      </Flex>
    </Flex>
  );
}
